import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { NgTemplateOutlet } from '@angular/common';
import { FilterChipComponent } from '../../components/filter-chip/filter-chip.component';
import { StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { ProjectRoleService } from '../../services/project-role.service';
import { ObjectEntryDialogComponent } from './object-entry-dialog/object-entry-dialog.component';
import { ObjectRenameDialogComponent } from './object-rename-dialog/object-rename-dialog.component';
import { ObjectMappingCreateDialogComponent } from './object-mapping-create-dialog/object-mapping-create-dialog.component';
import { ObjectUsageListComponent } from './object-usage-list/object-usage-list.component';
import { serverMessage } from './server-message';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { ObjectMappingService } from '../../services/object-mapping.service';
import { ObjectCaptureService } from '../../services/object-capture.service';
import {
  ENTRY_STATES,
  EntryState,
  HitKind,
  ObjectMappingEntryView,
  ObjectMappingLocator,
  ObjectMappingSummary,
  ObjectUsages,
  RenameResult,
} from '../../models/object-mapping.model';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Ab so vielen Tagen steht das Datum statt „vor N Tagen“. */
const RELATIVE_DAYS_LIMIT = 30;

const STATE_COUNTS: Record<EntryState, keyof ObjectMappingSummary> = {
  FRAGILE: 'fragileCount',
  UNUSED: 'unusedCount',
  NEVER_HIT: 'neverHitCount',
  MISSING: 'missingCount',
};

interface StateChip {
  key: EntryState | 'ALL';
  state: EntryState | null;
  count: number | null;
  active: boolean;
}

interface LastHit {
  when: string;
  /** 1-basiert, wie die Testerin die Kandidaten zaehlt. */
  index: number;
  kind: HitKind | null;
  /** i18n-Schluessel des Tooltips, wenn der Treffer brüchig ist. */
  warning: string | null;
}

interface Candidate {
  strategy: string;
  value: string;
  extras: string;
}

interface EntryRow {
  id: string;
  logicalName: string;
  typeName: string;
  candidates: Candidate[];
  lastHit: LastHit | null;
  missingSince: string | null;
  entry: ObjectMappingEntryView;
}

type LoadState = 'ok' | 'notFound' | 'error';

/** Offener Eintrag-Dialog; ohne `entry` wird angelegt. */
interface EntryDialogTarget {
  entry: ObjectMappingEntryView | null;
}

/** Offener Umbenennen-Dialog; ohne `entryId` fuer die ganze Bibliothek. */
interface RenameTarget {
  entryId?: string;
  name: string;
}

interface UsagesView {
  entryId: string;
  name: string;
  usages: ObjectUsages | null;
  error: boolean;
}

/** `usages` bleibt leer, bis das BE beim ersten DELETE mit 409 die Verwender meldet. */
interface LibraryDeleteView {
  usages: ObjectUsages | null;
  busy: boolean;
  error: string | null;
}

interface DeleteView extends LibraryDeleteView {
  row: EntryRow;
}

/**
 * Eintraege einer Objektbibliothek, seitenweise nachgeladen (NEX-1276).
 *
 * Design-Luecke: Das Bundle enthaelt keinen Entwurf fuer diese Seite. Aufbau folgt der
 * Testdatenblatt-Liste. Liegt ein Entwurf vor, gehoert die Seite angeglichen.
 */
@Component({
  selector: 'app-object-mapping-detail',
  imports: [
    TranslatePipe,
    RouterLink,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatTooltip,
    NgTemplateOutlet,
    FilterChipComponent,
    StatusPillComponent,
    ProjectNavComponent,
    PageHeaderComponent,
    SearchBarComponent,
    ConfirmDialogComponent,
    ObjectRenameDialogComponent,
    ObjectEntryDialogComponent,
    ObjectMappingCreateDialogComponent,
    ObjectUsageListComponent,
  ],
  templateUrl: './object-mapping-detail.component.html',
  styleUrl: './object-mapping-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectMappingDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(ObjectMappingService);
  private readonly captures = inject(ObjectCaptureService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  readonly isProjectReader = inject(ProjectRoleService).isProjectReader;

  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly mappingId: string = this.route.snapshot.params['mappingId'];

  readonly library = signal<ObjectMappingSummary | null>(null);
  readonly searchTerm = signal('');
  readonly entryState = signal<EntryState | null>(null);
  readonly loading = signal(true);
  readonly loadingMore = signal(false);
  readonly state = signal<LoadState>('ok');
  readonly moreError = signal(false);
  readonly last = signal(true);
  readonly total = signal(0);
  readonly expanded = signal<ReadonlySet<string>>(new Set());

  readonly renameTarget = signal<RenameTarget | null>(null);
  readonly entryDialog = signal<EntryDialogTarget | null>(null);
  readonly usagesView = signal<UsagesView | null>(null);
  readonly deleteView = signal<DeleteView | null>(null);
  readonly libraryDeleteView = signal<LibraryDeleteView | null>(null);
  readonly notice = signal<string | null>(null);
  /** Offene Aufnahmen; 0 auch, wenn sie nicht geladen werden konnten, denn der Hinweis ist nur ein Wegweiser. */
  readonly pendingCaptures = signal(0);
  readonly pendingCapturesKey = computed(() =>
    this.pendingCaptures() === 1 ? 'objectMappings.detail.pendingCapturesOne' : 'objectMappings.detail.pendingCaptures',
  );
  readonly reviewLink = ['/projects', this.projectId, 'object-mappings', this.mappingId, 'review'];

  readonly recordDialogOpen = signal(false);

  /** Aufnehmen gibt es nur fuer Browser-Bibliotheken (NEX-1719); solange der Kopf laedt, ist offen, welche Engine. */
  readonly recordable = computed(() => this.library()?.engineId === 'browser');

  private readonly entries = signal<ObjectMappingEntryView[]>([]);
  readonly rows = computed<EntryRow[]>(() => {
    const lang = this.translate.getCurrentLang() || 'de';
    const now = new Date();
    return this.entries().map((entry) => toRow(entry, lang, now));
  });
  readonly stateChips = computed<StateChip[]>(() => {
    const library = this.library();
    const active = this.entryState();
    const all: StateChip = { key: 'ALL', state: null, count: library?.entryCount ?? null, active: active === null };
    return [
      all,
      ...ENTRY_STATES.map((state) => ({
        key: state,
        state,
        count: (library?.[STATE_COUNTS[state]] as number | undefined) ?? null,
        active: active === state,
      })),
    ];
  });
  readonly canLoadMore = computed(() => !this.loading() && this.state() === 'ok' && !this.last());

  private readonly searchInput = new Subject<string>();
  private query = '';
  private nextPage = 0;
  private request: Subscription | null = null;
  /** Nach dem Speichern aufklappen, sobald die erste Seite ihn enthaelt. */
  private expandAfterLoad: string | null = null;

  constructor() {
    this.searchInput
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        map((term) => term.trim()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((q) => this.startSearch(q));
    this.destroyRef.onDestroy(() => this.request?.unsubscribe());
  }

  ngOnInit(): void {
    this.loadLibrary();
    this.captures
      .list(this.projectId, this.mappingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (list) => this.pendingCaptures.set(list.length), error: () => this.pendingCaptures.set(0) });
    // Abonniert statt aus dem Snapshot gelesen: Jede Chip-Wahl legt einen Verlaufseintrag an,
    // und nach „Zurueck“ soll der Filter der URL folgen.
    let first = true;
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('state')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((requested) => {
        const state = ENTRY_STATES.find((s) => s === requested) ?? null;
        if (requested !== null && !state) this.navigateToState(null, true);
        if (!first && state === this.entryState()) return;
        first = false;
        this.entryState.set(state);
        this.startSearch(this.query);
      });
  }

  selectState(state: EntryState | null): void {
    if (state === this.entryState()) return;
    this.entryState.set(state);
    this.navigateToState(state, false);
    this.startSearch(this.query);
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.searchInput.next(term);
  }

  retry(): void {
    this.startSearch(this.query);
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.loadingMore()) return;
    this.loadingMore.set(true);
    this.moreError.set(false);
    this.fetch();
  }

  isExpanded(id: string): boolean {
    return this.expanded().has(id);
  }

  toggle(id: string): void {
    const next = new Set(this.expanded());
    if (!next.delete(id)) next.add(id);
    this.expanded.set(next);
  }

  openRename(row?: EntryRow): void {
    this.notice.set(null);
    this.renameTarget.set(
      row ? { entryId: row.id, name: row.logicalName } : { name: this.library()?.name ?? this.mappingId },
    );
  }

  openEntryDialog(row?: EntryRow): void {
    this.notice.set(null);
    this.entryDialog.set({ entry: row?.entry ?? null });
  }

  onEntrySaved(saved: ObjectMappingEntryView): void {
    this.entryDialog.set(null);
    this.notice.set(this.translate.instant('objectMappings.detail.saved', { name: saved.logicalName }));
    this.expandAfterLoad = saved.id;
    this.loadLibrary();
    this.startSearch(this.query);
  }

  onRenamed(result: RenameResult): void {
    this.renameTarget.set(null);
    this.notice.set(
      this.translate.instant('objectMappings.detail.renamed', {
        oldName: result.oldName,
        newName: result.newName,
        count: result.occurrenceCount,
      }),
    );
    this.loadLibrary();
    this.startSearch(this.query);
  }

  showUsages(row: EntryRow): void {
    this.usagesView.set({ entryId: row.id, name: row.logicalName, usages: null, error: false });
    const forThisRow = (change: Partial<UsagesView>) =>
      this.usagesView.update((view) => (view?.entryId === row.id ? { ...view, ...change } : view));
    this.service
      .usages(this.projectId, this.mappingId, row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usages) => forThisRow({ usages }),
        error: () => forThisRow({ error: true }),
      });
  }

  deleteEntry(row: EntryRow): void {
    this.notice.set(null);
    this.deleteView.set({ row, usages: null, busy: false, error: null });
  }

  /**
   * Erst ohne `confirm`: Ohne Verwender loescht das BE sofort, sonst kommt 409 mit der Liste
   * und der Dialog fragt mit ihr noch einmal nach. Erst dann geht `confirm=true` raus.
   */
  confirmDelete(): void {
    const view = this.deleteView();
    if (!view || view.busy) return;
    this.deleteView.set({ ...view, busy: true, error: null });
    this.service
      .deleteEntry(this.projectId, this.mappingId, view.row.id, view.usages !== null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleteView.set(null);
          this.afterDelete(view.row);
        },
        error: (err: unknown) =>
          this.deleteView.update(
            (current) =>
              current && this.afterFailedDelete(current, !view.usages, err, 'objectMappings.detail.deleteError'),
          ),
      });
  }

  deleteLibrary(): void {
    this.notice.set(null);
    this.libraryDeleteView.set({ usages: null, busy: false, error: null });
  }

  /** Wie `confirmDelete`: erst ohne `confirm`, bei 409 mit Verwendern noch einmal fragen. */
  confirmLibraryDelete(): void {
    const view = this.libraryDeleteView();
    if (!view || view.busy) return;
    this.libraryDeleteView.set({ ...view, busy: true, error: null });
    this.service
      .deleteMapping(this.projectId, this.mappingId, view.usages !== null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.libraryDeleteView.set(null);
          this.router.navigate(['/projects', this.projectId, 'object-mappings']);
        },
        error: (err: unknown) =>
          this.libraryDeleteView.update(
            (current) =>
              current &&
              this.afterFailedDelete(current, !view.usages, err, 'objectMappings.detail.deleteLibrary.error'),
          ),
      });
  }

  private afterDelete(row: EntryRow): void {
    this.notice.set(this.translate.instant('objectMappings.detail.deleted', { name: row.logicalName }));
    this.loadLibrary();
    this.startSearch(this.query);
  }

  /** 409 mit Verwendern auf die erste Anfrage fuehrt zur zweiten Stufe, alles andere ist ein Fehler. */
  private afterFailedDelete<V extends LibraryDeleteView>(
    view: V,
    firstStep: boolean,
    err: unknown,
    errorKey: string,
  ): V {
    const usages = firstStep && err instanceof HttpErrorResponse && err.status === 409 ? err.error : null;
    return isUsages(usages)
      ? { ...view, usages, busy: false }
      : { ...view, busy: false, error: serverMessage(err) ?? this.translate.instant(errorKey) };
  }

  private startSearch(q: string): void {
    this.query = q;
    this.nextPage = 0;
    this.entries.set([]);
    this.expanded.set(new Set());
    this.loading.set(true);
    this.loadingMore.set(false);
    this.moreError.set(false);
    this.state.set('ok');
    this.fetch();
  }

  private fetch(): void {
    this.request?.unsubscribe();
    this.request = this.service
      .entries(this.projectId, this.mappingId, this.query, this.nextPage, PAGE_SIZE, this.entryState())
      .subscribe({
        next: (page) => {
          this.entries.update((loaded) => [...loaded, ...page.content]);
          const wanted = this.expandAfterLoad;
          this.expandAfterLoad = null;
          if (wanted && page.content.some((e) => e.id === wanted)) this.expanded.set(new Set([wanted]));
          this.nextPage = page.number + 1;
          this.last.set(page.last);
          this.total.set(page.totalElements);
          this.loading.set(false);
          this.loadingMore.set(false);
        },
        error: (err: unknown) => {
          this.expandAfterLoad = null;
          if (this.loadingMore()) {
            this.moreError.set(true);
          } else {
            this.state.set(err instanceof HttpErrorResponse && err.status === 404 ? 'notFound' : 'error');
          }
          this.loading.set(false);
          this.loadingMore.set(false);
        },
      });
  }

  /** Ohne `replaceUrl` bleibt der Filter in der Verlaufsliste; ein ungueltiger Wert nicht. */
  private navigateToState(state: EntryState | null, replaceUrl: boolean): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { state },
      queryParamsHandling: 'merge',
      ...(replaceUrl ? { replaceUrl } : {}),
    });
  }

  private loadLibrary(): void {
    this.service
      .listAll(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (all) => this.library.set(all.find((lib) => lib.id === this.mappingId) ?? null),
        // Beim ersten Laden faellt der Kopf auf einen allgemeinen Titel zurueck; spaeter bleibt der
        // bekannte Stand stehen. Die Eintraege laden in beiden Faellen.
        error: () => undefined,
      });
  }
}

function isUsages(body: unknown): body is ObjectUsages {
  return !!body && typeof body === 'object' && Array.isArray((body as ObjectUsages).testCases);
}

function toRow(entry: ObjectMappingEntryView, lang: string, now: Date): EntryRow {
  return {
    id: entry.id,
    logicalName: entry.logicalName,
    typeName: entry.typeName ?? '',
    candidates: (entry.locators ?? []).map(toCandidate),
    lastHit: entry.lastHitAt ? toLastHit(entry, entry.lastHitAt, lang, now) : null,
    missingSince: entry.missingSince ? formatDay(new Date(entry.missingSince)) : null,
    entry,
  };
}

function toLastHit(entry: ObjectMappingEntryView, at: string, lang: string, now: Date): LastHit {
  const index = entry.lastHitIndex ?? 0;
  const kind = entry.lastHitKind ?? null;
  let warning: string | null = null;
  if (index > 0) warning = 'objectMappings.state.laterCandidate';
  else if (kind === 'POSITION') warning = 'objectMappings.state.positionOnly';
  return { when: relativeDay(new Date(at), lang, now), index: index + 1, kind, warning };
}

/** Nach Kalendertagen, nicht nach 24-Stunden-Spannen: 23:55 gestern ist „gestern“. */
function relativeDay(then: Date, lang: string, now: Date): string {
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY_MS);
  if (days >= RELATIVE_DAYS_LIMIT) return formatDay(then);
  return new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }).format(-days, 'day');
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function formatDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

function toCandidate(locator: ObjectMappingLocator): Candidate {
  const { strategy = '', value = '', ...rest } = locator;
  return {
    strategy,
    value,
    extras: Object.entries(rest)
      .map(([key, val]) => `${key}=${val}`)
      .join(' · '),
  };
}
